import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { API_CONFIG } from '../../apis/api-config';
import { getStorage } from '../../hooks/useAuth';
import { createCircuitFromRow } from '../../utils/circuitMapper';
import { useAssetsStore } from '../../hooks/useAssetsStore';
import { loadAssetsThunk } from '../../store/assets/assetsThunks';
import { AppDispatch } from '../../store';
import { HomeButton } from '../../components/homeButton/homeButton';
import { useTranslation } from 'react-i18next';
import "./InterpretJSON.css"


interface TableCell {
    isHeader: boolean;
    rowIndex: number;
    columnIndex: number;
    rowSpan: number;
    columnSpan: number;
    content: string;
}

interface TableData {i
    name: string;
    language: string;
    rowCount: number;
    columnCount: number;
    cells: TableCell[];
}

const convertTableData = (newTableData: TableData) => {
    const headers: string[] = [];
    const rows: string[][] = [];

    // Initialize arrays based on table dimensions
    for (let i = 0; i < newTableData.rowCount; i++) {
        rows[i] = new Array(newTableData.columnCount).fill('');
    }

    let headerRowIndex = -1;

    // Process all cells
    newTableData.cells.forEach(cell => {
        if (cell.isHeader) {
            // Track which row contains headers
            if (headerRowIndex === -1) {
                headerRowIndex = cell.rowIndex;
            }
            // Ensure headers array is large enough
            while (headers.length <= cell.columnIndex) {
                headers.push('');
            }
            headers[cell.columnIndex] = cell.content;
        } else {
            // Place content in the appropriate row/column
            if (rows[cell.rowIndex]) {
                rows[cell.rowIndex][cell.columnIndex] = cell.content;
            }
        }
    });

    // Remove header row from data rows if it exists
    const dataRows = headerRowIndex >= 0 ? 
        rows.filter((_, index) => index !== headerRowIndex) : 
        rows;

    // Filter out empty rows
    const filteredRows = dataRows.filter(row => 
        row.some(cell => cell && cell.trim() !== '')
    );

    return {
        name: newTableData.name,
        headers: headers.filter(h => h !== ''), // Remove empty headers
        rows: filteredRows
    };
};

const InterpretJSON: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const assetId = searchParams.get('assetId');
    
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [ocrResults, setOcrResults] = useState<any[]>([]); // Using converted format
    const [rawApiResults, setRawApiResults] = useState<TableData[]>([]); // Store raw API response
    const [error, setError] = useState<string | null>(null);
    const [headerMappings, setHeaderMappings] = useState<{ [key: number]: string }>({});
    const [mappingModal, setMappingModal] = useState<{ isOpen: boolean, headerIndex: number, headerName: string }>({
        isOpen: false,
        headerIndex: -1,
        headerName: ''
    });
    const [editingCell, setEditingCell] = useState<{ rowIndex: number, cellIndex: number } | null>(null);
    const [editValue, setEditValue] = useState<string>('');

    const { getAssetById, loading: storeLoading, loaded: storeLoaded } = useAssetsStore();
    const existingAsset = getAssetById(assetId || '');

    const { t } = useTranslation();

    useEffect(() => {
        if (!storeLoaded && !storeLoading) {
            console.log('Loading assets from Redux store...');
            dispatch(loadAssetsThunk());
        }
    }, [dispatch, storeLoaded, storeLoading]);

    useEffect(() => {
        if (assetId && storeLoaded && !existingAsset && !storeLoading) {
            console.log(`Asset ${assetId} not found in store, refreshing assets...`);
            dispatch(loadAssetsThunk());
        }
    }, [dispatch, assetId, storeLoaded, existingAsset, storeLoading]);

    if (assetId && !existingAsset && storeLoaded) {
        return (
            <div className="interpret-json-container">
                <div className="error">
                    Asset with ID {assetId} not found.
                    Wait for asset to arrive max 15 sec, if not contact dev
                    <HomeButton />
                </div>
            </div>
        );
    }

    const TABLE_MAPPING_OPTIONS = [
        { value: 'circuitId', label: t('di.circuitNr', 'Circuit Nr') },
        { value: 'description', label: t('di.circuitDescription', 'Circuit Description') },
        { value: 'maxCurrent', label: t('di.maxCurrent', 'Maximum Current (Fuse Rating)') },
        { value: 'continousCurrent', label: t('di.ContinousCurrent', 'Continuous Current') },
        { value: 'wireDimentions', label: t('di.WireDimentions', 'Wire Dimensions (Cable Size)') },
        { value: 'meteredBy', label: t('di.MeteredBy', 'Metered By') },
        { value: 'ignore', label: t('di.ignore', '--- Ignore this column ---') }
    ];

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            setError(null);
        }
    };

    const uploadAndAnalyze = async () => {
        if (!selectedFile) {
            setError('Please select a file first');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('file', selectedFile);

            const token = getStorage().getItem('access_token');

            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DISTRB_OCR}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const rawData: TableData[] = await response.json();
            console.log('Raw API response:', rawData);
            
            // Store raw data and convert for display
            setRawApiResults(rawData);
            const convertedData = rawData.map(convertTableData);
            console.log('Converted data:', convertedData);
            setOcrResults(convertedData);

        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred during upload');
            console.error('Upload error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const removeTable = (indexToRemove: number) => {
        setOcrResults(prevResults =>
            prevResults.filter((_, index) => index !== indexToRemove)
        );
        setRawApiResults(prevResults =>
            prevResults.filter((_, index) => index !== indexToRemove)
        );
        setHeaderMappings({});
    };

    const handleMappingChange = (headerIndex: number, mappingValue: string) => {
        setHeaderMappings(prev => ({
            ...prev,
            [headerIndex]: mappingValue
        }));
        setMappingModal({ isOpen: false, headerIndex: -1, headerName: '' });
    };

    const openMappingModal = (headerIndex: number, headerName: string) => {
        setMappingModal({ isOpen: true, headerIndex, headerName });
    };

    const closeMappingModal = () => {
        setMappingModal({ isOpen: false, headerIndex: -1, headerName: '' });
    };

    const startCellEdit = (rowIndex: number, cellIndex: number, currentValue: string) => {
        setEditingCell({ rowIndex, cellIndex });
        setEditValue(currentValue);
    };

    const cancelCellEdit = () => {
        setEditingCell(null);
        setEditValue('');
    };

    const saveCellEdit = () => {
        if (editingCell && ocrResults.length === 1) {
            const updatedResults = [...ocrResults];
            updatedResults[0].rows[editingCell.rowIndex][editingCell.cellIndex] = editValue;
            setOcrResults(updatedResults);
            
            // Also update the raw API results to keep them in sync
            const updatedRawResults = [...rawApiResults];
            const rawTable = updatedRawResults[0];
            const targetCell = rawTable.cells.find(cell => 
                !cell.isHeader && 
                cell.rowIndex === editingCell.rowIndex + 1 && // Adjust for header row
                cell.columnIndex === editingCell.cellIndex
            );
            if (targetCell) {
                targetCell.content = editValue;
            }
            setRawApiResults(updatedRawResults);
            
            setEditingCell(null);
            setEditValue('');
        }
    };

    const handleCellKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            saveCellEdit();
        } else if (e.key === 'Escape') {
            cancelCellEdit();
        }
    };

    const generateCircuits = async () => {
        if (ocrResults.length !== 1 || !assetId || !existingAsset) {
            const errorMsg = `Cannot generate circuits: ${
                ocrResults.length !== 1 ? `need exactly one table (got ${ocrResults.length})` : 
                !assetId ? 'missing asset ID' : 
                !existingAsset ? `asset ${assetId} not found in store` : 'unknown error'
            }`;
            setError(errorMsg);
            return;
        }

        const table = ocrResults[0];

        try {
            setIsLoading(true);
            setError(null);

            const distributionBoardCircuits = table.rows.map((row: string[], index: number) =>
                createCircuitFromRow(row, index, headerMappings)
            );

            const updatePayload = {
                ...existingAsset,
                assetDistributionBoard: {
                    ...existingAsset?.assetDistributionBoard,
                    distributionBoardCircuits: distributionBoardCircuits
                }
            };

            if (existingAsset.assetType && existingAsset.assetType.id) {
                (updatePayload as any).assetTypeId = existingAsset.assetType.id;
            }

            console.log('Sending payload with assetTypeId:', (updatePayload as any).assetTypeId);

            const token = getStorage().getItem('access_token');
            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ASSETS}/${assetId}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(updatePayload)
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Error response body:', errorText);
                throw new Error(`Failed to update asset: ${response.status}. Details: ${errorText}`);
            }

            const result = await response.json();
            console.log('Update successful:', result);

            alert(`${t('added','Added')} ${distributionBoardCircuits.length} ${t('circuits','circuits')}!`);
            navigate('/home');

        } catch (err) {
            console.error('Error updating distribution board:', err);
            setError(err instanceof Error ? err.message : 'Failed to update distribution board');
        } finally {
            setIsLoading(false);
        }
    };

    if (storeLoading || (!storeLoaded && assetId)) {
        return (
            <div className="interpret-json-container">
                <div className="loading">
                    <span className="loading-spinner"></span>
                    Loading asset data...
                </div>
            </div>
        );
    }

    if (assetId && storeLoaded && !existingAsset) {
        return (
            <div className="interpret-json-container">
                <div className="error">
                    Asset with ID {assetId} not found. 
                    <button onClick={() => dispatch(loadAssetsThunk())}>
                        Retry Loading Assets
                    </button>
                </div>
            </div>
        );
    }

    const renderTable = (table: any, index: number) => (
        <div key={index} className="table-container">
            {ocrResults.length > 1 && (
                <button
                    onClick={() => removeTable(index)}
                    className="remove-button"
                    title="Remove this table"
                >
                    ×
                </button>
            )}

            <div className="table-header">
                <h3 className="table-title">{t('energy:tableView','Table')} {index + 1}</h3>
                {ocrResults.length === 1 && (
                    <p className="mapping-instructions">
                        {t('di.tapHeaders','Tap headers to map columns to distribution board fields')}
                    </p>
                )}
            </div>
            {table.name && <p className="table-name">Name: {table.name}</p>}

            <div className="table-wrapper">
                <table className="data-table">
                    <thead>
                        <tr>
                            {table.headers.map((header: string, headerIndex: number) => (
                                <th
                                    key={headerIndex}
                                    className={`table-header-cell ${ocrResults.length === 1 ? 'clickable-header' : ''} ${headerMappings[headerIndex] ? 'mapped-header' : ''}`}
                                    onClick={ocrResults.length === 1 ? () => openMappingModal(headerIndex, header) : undefined}
                                    title={ocrResults.length === 1 ? 'Click to map this column' : ''}
                                >
                                    <div className="header-content">
                                        <span className="header-text">{header}</span>
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {table.rows.map((row: string[], rowIndex: number) => (
                            <tr key={rowIndex}>
                                {row.map((cell: string, cellIndex: number) => {
                                    const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.cellIndex === cellIndex;
                                    const isEditable = ocrResults.length === 1;

                                    return (
                                        <td
                                            key={cellIndex}
                                            className={`table-cell ${isEditable ? 'editable-cell' : ''} ${isEditing ? 'editing-cell' : ''}`}
                                            onClick={isEditable && !isEditing ? () => startCellEdit(rowIndex, cellIndex, cell) : undefined}
                                            title={isEditable && !isEditing ? 'Click to edit this cell' : ''}
                                        >
                                            {isEditing ? (
                                                <input
                                                    type="text"
                                                    value={editValue}
                                                    onChange={(e) => setEditValue(e.target.value)}
                                                    onKeyDown={handleCellKeyDown}
                                                    onBlur={saveCellEdit}
                                                    className="cell-edit-input"
                                                    autoFocus
                                                />
                                            ) : (
                                                <span>{cell}</span>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {ocrResults.length === 1 && (
                <div className="generate-section">
                    <button
                        onClick={generateCircuits}
                        className="generate-button"
                        disabled={Object.keys(headerMappings).length === 0 || isLoading}
                    >
                        {isLoading ? `${t('uploading','Uploading')}` : `${t('di.headerAdd','Add Circuits to Distribution Board ')}`}
                    </button>
                </div>
            )}
        </div>
    );

    return (
        <div className="interpret-json-container">
            <h1 className="main-title">
                {assetId ? `${t('di.headerAdd','Add Circuits to Distribution Board ')}`: 'Distribution Board Interpreter'}
            </h1>

            {assetId && existingAsset && (
                <div className="existing-asset-info">
                    <p><strong>{t('admin:slideShows.asset','Asset')}:</strong> {existingAsset.name}</p>
                    <p><strong>{t('admin:slideShows.asset','Asset')} ID:</strong> {assetId}</p>
                </div>
            )}

            <div className="upload-section">
                <div className="upload-dropzone">
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        className="hidden"
                        id="file-upload"
                    />
                    <label htmlFor="file-upload" className="upload-label">
                        {selectedFile ? selectedFile.name : 'Click to select an image file'}
                    </label>
                </div>

                <button
                    onClick={uploadAndAnalyze}
                    disabled={!selectedFile || isLoading}
                    className="upload-button"
                >
                    {isLoading && <span className="loading-spinner"></span>}
                    {isLoading ? `${t('analyzing','Analyzing')}...` : `${t('upload','Upload')} & ${t('analyze','Analyze')}`}
                </button>
            </div>

            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}

            {ocrResults.length > 0 && (
                <div className="results-section">
                    <h2 className="results-title">
                        {t('found','Found')} {ocrResults.length} {t('di.tablesInImage','table(s) in the image')}:
                    </h2>
                    {ocrResults.map((table, index) => renderTable(table, index))}
                </div>
            )}

            {mappingModal.isOpen && (
                <div className="ijson-modal-overlay" onClick={closeMappingModal}>
                    <div className="ijson-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="ijson-modal-header">
                            <h3 className="ijson-modal-title">Map Column: "{mappingModal.headerName}"</h3>
                            <button className="ijson-modal-close" onClick={closeMappingModal}>×</button>
                        </div>

                        <div className="ijson-modal-body">
                            <p className="ijson-modal-description">
                                Select which distribution board field this column should map to:
                            </p>

                            <div className="ijson-mapping-options">
                                {TABLE_MAPPING_OPTIONS.map(option => (
                                    <button
                                        key={option.value}
                                        className={`ijson-mapping-option ${headerMappings[mappingModal.headerIndex] === option.value ? 'selected' : ''}`}
                                        onClick={() => handleMappingChange(mappingModal.headerIndex, option.value)}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InterpretJSON;